import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, UserPlus, X, Crown, Shield, User as UserIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { JoinLinkPanel } from "@/components/JoinLinkPanel";
import { UserAvatar } from "@/components/UserAvatar";

const ROLE_META = {
  head: { label: "Head", icon: Crown, badge: "default" },
  co_leader: { label: "Co-Leader", icon: Shield, badge: "secondary" },
  member: { label: "Member", icon: UserIcon, badge: "outline" },
};

function RoleBadge({ role }) {
  const meta = ROLE_META[role] || ROLE_META.member;
  const Icon = meta.icon;
  return (
    <Badge variant={meta.badge} className="gap-1" data-testid={`role-badge-${role}`}>
      <Icon className="h-3 w-3" /> {meta.label}
    </Badge>
  );
}

export default function MyMinistry() {
  const { user } = useAuth();
  const [ministries, setMinistries] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [parishes, setParishes] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [parishId, setParishId] = useState("");
  const [ministryName, setMinistryName] = useState("");
  const [creating, setCreating] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviting, setInviting] = useState(false);

  const loadMinistries = () => {
    api.get("/orgs/my-ministries").then(({ data }) => {
      setMinistries(data);
      if (data.length > 0) setSelectedId((prev) => prev || data[0].id);
    });
  };

  useEffect(() => {
    loadMinistries();
    api.get("/orgs/parishes").then(({ data }) => setParishes(data));
    api.get("/orgs/ministry-suggestions").then(({ data }) => setSuggestions(data));
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    api.get(`/orgs/ministries/${selectedId}`).then(({ data }) => setDetail(data));
  }, [selectedId]);

  const myRole = ministries?.find((m) => m.id === selectedId)?.my_role;
  const canManage = user?.role === "admin" || myRole === "head" || myRole === "co_leader";

  const handleCreate = async () => {
    if (!parishId || !ministryName.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/orgs/ministries", { parish_id: parishId, name: ministryName.trim() });
      toast.success(`${data.name} created — you're the Ministry Head`);
      setCreateOpen(false);
      setMinistryName("");
      loadMinistries();
      setSelectedId(data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create ministry");
    } finally {
      setCreating(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const { data } = await api.post(`/orgs/ministries/${selectedId}/invite`, { email: inviteEmail.trim(), role: inviteRole });
      setDetail((prev) => ({ ...prev, members: data }));
      setInviteEmail("");
      toast.success("Member added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not invite member");
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (email) => {
    try {
      const { data } = await api.delete(`/orgs/ministries/${selectedId}/members`, { params: { email } });
      setDetail((prev) => ({ ...prev, members: data }));
      toast.success("Member removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove member");
    }
  };

  return (
    <div className="min-h-screen bg-[var(--brand-bg)] dark:bg-[var(--brand-bg-dark)]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-sacred text-3xl text-[var(--brand-navy)] dark:text-amber-100">My Ministry</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage your ministry roster and members</p>
          </div>
          <Button onClick={() => setCreateOpen(true)} className="bg-[var(--brand-crimson)] hover:bg-[var(--brand-crimson-hover)] text-white" data-testid="create-ministry-button">
            <Plus className="h-4 w-4 mr-2" /> Create Ministry
          </Button>
        </div>

        {ministries === null && <p className="text-muted-foreground font-display italic">Loading...</p>}

        {ministries && ministries.length === 0 && (
          <div className="text-center py-20 border border-dashed border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] rounded-lg" data-testid="no-ministries-state">
            <p className="font-display text-xl text-muted-foreground italic">You're not part of a ministry yet. Create one to get started.</p>
          </div>
        )}

        {ministries && ministries.length > 0 && (
          <div className="flex gap-2 mb-6 flex-wrap" data-testid="ministry-tabs">
            {ministries.map((m) => (
              <button
                key={m.id}
                onClick={() => setSelectedId(m.id)}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${selectedId === m.id ? "bg-[var(--brand-navy)] text-white border-[var(--brand-navy)]" : "border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] hover:bg-[var(--brand-bg)]"}`}
                data-testid={`ministry-tab-${m.id}`}
              >
                {m.name}
              </button>
            ))}
          </div>
        )}

        {detail && (
          <div className="bg-white dark:bg-[var(--brand-card-dark)] border border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] rounded-lg p-6" data-testid="ministry-roster">
            <h2 className="font-display text-xl font-semibold mb-4">{detail.name} &mdash; Roster</h2>
            <div className="space-y-2 mb-6">
              {detail.members.map((m) => (
                <div key={m.email} className="flex items-center justify-between px-3 py-2 rounded-md bg-[var(--brand-bg)] dark:bg-[var(--brand-bg-dark)]" data-testid={`member-row-${m.email}`}>
                  <div className="flex items-center gap-3">
                    <UserAvatar name={m.name} email={m.email} picture={m.picture} className="h-8 w-8" testId={`member-avatar-${m.email}`} />
                    <div className="leading-tight">
                      {m.name && <p className="text-sm font-medium">{m.name}</p>}
                      <p className={m.name ? "text-xs text-muted-foreground" : "text-sm"}>{m.email}</p>
                    </div>
                    <RoleBadge role={m.role} />
                  </div>
                  {canManage && m.role !== "head" && (
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleRemove(m.email)} data-testid={`remove-member-${m.email}`}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {canManage && (
              <div className="pt-4 border-t border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] space-y-3">
                <p className="text-sm font-medium">Add a member</p>
                <div className="flex gap-2">
                  <Input type="email" placeholder="leader@example.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} data-testid="member-invite-email-input" />
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger className="w-36" data-testid="member-invite-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="co_leader">Co-Leader</SelectItem>
                      <SelectItem value="member">Member</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={handleInvite} disabled={inviting} data-testid="member-invite-submit-button">
                    <UserPlus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            {canManage && <JoinLinkPanel ministryId={selectedId} ministryName={detail.name} />}
          </div>
        )}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-ministry-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Create a Ministry</DialogTitle>
            <DialogDescription>You'll automatically become the Ministry Head.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Parish</Label>
              <Select value={parishId} onValueChange={setParishId}>
                <SelectTrigger data-testid="create-ministry-parish-select"><SelectValue placeholder="Select a parish" /></SelectTrigger>
                <SelectContent>
                  {parishes.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ministry name</Label>
              <Input value={ministryName} onChange={(e) => setMinistryName(e.target.value)} placeholder="e.g. Intercession Ministry" data-testid="create-ministry-name-input" />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => setMinistryName(s)}
                    className="text-xs px-2.5 py-1 rounded-full border border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] hover:bg-[var(--brand-bg)] dark:hover:bg-[var(--brand-bg-dark)]"
                    data-testid={`ministry-suggestion-${s.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <Button onClick={handleCreate} disabled={creating || !parishId || !ministryName.trim()} className="w-full bg-[var(--brand-crimson)] hover:bg-[var(--brand-crimson-hover)] text-white" data-testid="submit-create-ministry-button">
              {creating ? "Creating..." : "Create Ministry"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
